"use client";
import { useState, useEffect } from "react";
import { Input, Label, Button, Heading } from "@postcheesy/ui-lib/atoms";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, Checkbox, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, Toaster, useToast } from "@postcheesy/ui-lib/molecules";
import { LogoutButton } from "./LogoutButton";
import { useUser } from "../../resources/contexts/UserContext";
import { getMagasins, Magasin } from "../../api/getMagasin";
import { ManageInfoUpdateRequest, updateManageInfo, User } from "../../api/manageInfo";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../resources/constants/routes";


interface ProfileCardProps {
    setup?: boolean
}

export const ProfileCard: React.FC<ProfileCardProps> = ({ setup }) => {
    const { user, loading, refreshUser, isActive } = useUser();
    const [profile, setProfile] = useState(user);
    const [initialProfile, setInitialProfile] = useState(user);
    const isValid = profile?.prenom && profile?.nom && profile?.magasinId;
    const [magasins, setMagasins] = useState<Magasin[]>([]);
    const navigate = useNavigate();
    const { toast } = useToast();


    useEffect(() => {
        if (!user && setup && !loading) {
            refreshUser();
        }
    }, [user, setup, loading, refreshUser]);

    useEffect(() => {
        if (!setup || !user || loading) return;
        if (isActive) {
            navigate(ROUTES.DASHBOARD, { replace: true });
        } else if (!user?.isEmailConfirmed) {
            navigate(ROUTES.CONFIRM_EMAIL, { replace: true });
        } else if (!user?.isPhoneConfirmed) {
            navigate(ROUTES.VERIFY_PHONE, { replace: true });
        }
    }, [user, isActive, navigate, setup, loading]);

    useEffect(() => {
        if (user && !loading && JSON.stringify(user) !== JSON.stringify(initialProfile)) {
            setProfile(user);
            setInitialProfile(user);
        }
    }, [user, loading]);

    useEffect(() => {
        const fetchMagasins = async () => {
            try {
                const data = await getMagasins();
                if (data) setMagasins(data);
            } catch (error) {
                console.error("Erreur lors du chargement des magasins:", error);
            }
        };
        fetchMagasins();
    }, []);


    const handleChange = (key: keyof User, value: any) => {
        setProfile((prevProfile) => ({
            ...prevProfile,
            [key]: value,
        }) as User | null);
    };

    const handleSave = async () => {
        if (!profile) {
            toast({
                title: "Erreur",
                description: "Impossible de mettre à jour le profil, les données sont invalides.",
                variant: "destructive",
            });
            return;
        }

        if (!isValid) {
            toast({
                title: "Champs obligatoires manquants",
                description: "Veuillez remplir tous les champs obligatoires avant de sauvegarder.",
                variant: "destructive",
            });
            return;
        }

        // Convert User to ManageInfoUpdateRequest for post
        const updateRequest: ManageInfoUpdateRequest = {
            nom: profile.nom || "",
            prenom: profile.prenom || "",
            codePostal: profile.codePostal || "",
            magasinId: profile.magasinId ? Number(profile.magasinId) : undefined,
            isEmailArrivageCoupDeCoeur: profile.isEmailArrivageCoupDeCoeur ?? false,
            isEmailEvents: profile.isEmailEvents ?? false,
            isEmailWhatIsNewExclusif: profile.isEmailWhatIsNewExclusif ?? false,
            oldPassword: "",
            newEmail: undefined,
            newPassword: undefined,
        };

        try {
            const updatedUser = await updateManageInfo(updateRequest);
            if (updatedUser) {
                refreshUser();
                setInitialProfile(updatedUser);
                toast({
                    title: "Succès",
                    description: "Votre profil a été mis à jour avec succès.",
                    variant: "default",
                });
                
                // Check if user is now active and redirect to dashboard
                if (setup && updatedUser.isEmailConfirmed && updatedUser.isPhoneConfirmed && 
                    updatedUser.nom && updatedUser.prenom && updatedUser.magasinId) {
                    navigate(ROUTES.DASHBOARD, { replace: true });
                }
            }
            else {
                throw new Error("Échec de la mise à jour du profil.");
            }
        } catch (error) {
            toast({
                title: "Erreur",
                description: "Une erreur est survenue lors de la mise à jour du profil.",
                variant: "destructive",
            });
        }
    };
    const isChanged = JSON.stringify(profile) !== JSON.stringify(initialProfile);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <Skeleton className="w-16 h-16 rounded-full" />
            </div>
        );
    }

    return (
        <Card className="h-full">
            <Toaster />
            {setup ?
                <CardHeader className="flex items-center justify-between">
                    <Heading size="default" className="text-xl font-semibold">
                        Finalisation de votre profil
                    </Heading>
                    <CardDescription className="w-fit text-pretty text-center">
                        Quelques informations supplémentaires sont requises pour complèter votre profil.
                    </CardDescription>
                </CardHeader>
                :
                <CardHeader className="flex items-center justify-between">
                    <Heading size="default" className="text-xl font-semibold">
                        {initialProfile?.prenom} {initialProfile?.nom}
                    </Heading>
                </CardHeader>
            }
            <CardDescription className="flex flex-col p-4">
                <div className="grid md:grid-cols-2 gap-4">
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Prénom <span className="text-xs text-primary">*</span></span>
                        <Input value={profile?.prenom || ""} onChange={(e) => handleChange("prenom", e.target.value)} required />
                    </Label>
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Nom <span className="text-xs text-primary">*</span></span>
                        <Input value={profile?.nom || ""} onChange={(e) => handleChange("nom", e.target.value)} required />
                    </Label>
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Courriel <span className="text-xs text-primary">*</span></span>
                        <Input type="email" value={profile?.email || ""} disabled />
                    </Label>
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Téléphone <span className="text-xs text-primary">*</span></span>
                        <Input type="tel" value={profile?.phone || ""} disabled />
                    </Label>
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Code postal</span>
                        <Input value={profile?.codePostal || ""} onChange={(e) => handleChange("codePostal", e.target.value)} />
                    </Label>
                    <Label>
                        <span className="text-sm font-medium text-muted-foreground">Ma succursale <span className="text-xs text-primary">*</span></span>
                        <Select
                            value={profile?.magasinId ? profile.magasinId.toString() : "none"}
                            onValueChange={(value) => handleChange("magasinId", value === "none" ? undefined : Number(value))}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Sélectionnez un magasin" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none" disabled>Aucune</SelectItem>
                                {magasins.map((magasin) => (
                                    <SelectItem key={magasin.id} value={magasin.id.toString()}>
                                        {magasin.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Label>
                    {!setup &&
                        <Button className="underline w-fit text-sm" variant={"ghost"} onClick={() => navigate(ROUTES.CHANGE_PASSWORD, { replace: true })}>
                            Changer mon mot de passe
                        </Button>
                    }
                </div>
                <div className="flex flex-col gap-2 pt-4">
                    <span className="text-sm">Je souhaite me faire aviser par courriel :</span>
                    <CardContent className="flex items-start gap-2 flex-col max-sm:gap-1">
                        <Label className="flex items-center space-x-2">
                            <Checkbox checked={profile?.isEmailArrivageCoupDeCoeur || false} onCheckedChange={(checked) => handleChange("isEmailArrivageCoupDeCoeur", checked)} />
                            <span className="text-sm">Des arrivages de mes fromages coup de cœur</span>
                        </Label>
                        <Label className="flex items-center space-x-2">
                            <Checkbox checked={profile?.isEmailEvents || false} onCheckedChange={(checked) => handleChange("isEmailEvents", checked)} />
                            <span className="text-sm">Des événements, ateliers et dégustations</span>
                        </Label>
                        <Label className="flex items-center space-x-2">
                            <Checkbox checked={profile?.isEmailWhatIsNewExclusif || false} onCheckedChange={(checked) => handleChange("isEmailWhatIsNewExclusif", checked)} />
                            <span className="text-sm">Des nouveautés et des exclusivités Yannick Fromagerie</span>
                        </Label>
                    </CardContent>
                </div>
            </CardDescription>
            <CardFooter className="flex items-center justify-between">
                <Button onClick={handleSave}
                    disabled={!isChanged}
                    variant={isChanged && isValid ? "primary" : "outline"}
                >
                    Enregistrer
                </Button>
                {!setup && <LogoutButton variant="destructive">Déconnexion</LogoutButton>}
            </CardFooter>
            <span className="text-xs text-muted-foreground p-4"><span className="text-xs text-primary">*</span>Champs obligatoires au fonctionnement de l'application</span>
        </Card >
    );
};